import { Controller } from '@hotwired/stimulus'

interface QueryString {
  [key: string]: string
}

interface QueryInputs {
  container: HTMLDivElement
  fieldSelect: HTMLSelectElement
  valueInput: HTMLInputElement
  label: HTMLLabelElement
}

export default class extends Controller {
  static targets = ['input']

  declare inputTarget: HTMLInputElement
  private queryInputGroups: QueryInputs[] = []
  private readonly searchableFields = [
    { value: 'all', label: 'All Fields' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'last_four_of_ssn', label: 'Last 4 SSN' },
  ]

  connect() {
    this.createInputContainer()
    this.addQueryInputs()
    this.createAddButton()
    this.initializeValues()
  }

  private createInputContainer() {
    const container = document.createElement('div')
    container.className = 'query-strings-container'
    this.inputTarget.parentElement?.appendChild(container)
  }

  private createAddButton() {
    const button = document.createElement('button')
    button.type = 'button'
    button.className =
      'button button__medium button__secondary--blue add-query-button'
    button.textContent = 'Add Field'
    button.addEventListener('click', () => this.addQueryInputs())
    this.inputTarget.parentElement?.appendChild(button)
  }

  private addQueryInputs(): QueryInputs {
    const container = document.createElement('div')
    container.className = 'query-strings-inputs'

    const groupId = `query-group-${this.queryInputGroups.length + 1}`

    const fieldSelect = document.createElement('select')
    fieldSelect.className = 'select-filter'
    fieldSelect.id = `${groupId}-field`
    fieldSelect.setAttribute('aria-label', 'Search field')

    this.searchableFields.forEach((option) => {
      const optionElement = document.createElement('option')
      optionElement.value = option.value
      optionElement.textContent = option.label
      fieldSelect.appendChild(optionElement)
    })

    const label = document.createElement('label')
    label.htmlFor = `${groupId}-value`
    label.textContent = 'Search value'
    label.className = 'form-field-label--visually_hidden'

    const valueInput = document.createElement('input')
    valueInput.type = 'text'
    valueInput.className = 'search-field'
    valueInput.id = `${groupId}-value`
    valueInput.placeholder = 'Search...'

    if (this.queryInputGroups.length > 0) {
      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className =
        'button button__small button__danger remove-query-button'
      removeButton.textContent = 'Remove'
      removeButton.setAttribute('aria-label', 'Remove search field')
      removeButton.addEventListener('click', () =>
        this.removeQueryInputs(container),
      )
      container.appendChild(removeButton)
    }

    fieldSelect.addEventListener('change', () => this.updateHiddenInput())
    valueInput.addEventListener('input', () => this.updateHiddenInput())

    container.appendChild(fieldSelect)
    container.appendChild(label)
    container.appendChild(valueInput)

    const parentContainer = this.inputTarget.parentElement.querySelector(
      '.query-strings-container',
    )
    parentContainer.appendChild(container)

    const queryInputs = { container, fieldSelect, valueInput, label }
    this.queryInputGroups.push(queryInputs)

    return queryInputs
  }

  private removeQueryInputs(container: HTMLDivElement) {
    const index = this.queryInputGroups.findIndex(
      (group) => group.container === container,
    )
    if (index !== -1) {
      this.queryInputGroups.splice(index, 1)
      container.remove()
      this.updateHiddenInput()
    }
  }

  private initializeValues() {
    const currentValue = this.getValue()

    this.queryInputGroups = []
    const container = this.inputTarget.parentElement?.querySelector(
      '.query-strings-container',
    )
    if (container) container.innerHTML = ''

    currentValue.forEach((queryString, index) => {
      const inputs = this.addQueryInputs()
      const [field, value] = [
        Object.keys(queryString)[0],
        queryString[Object.keys(queryString)[0]],
      ]
      inputs.fieldSelect.value = field
      inputs.valueInput.value = value
    })

    if (this.queryInputGroups.length === 0) {
      this.addQueryInputs()
    }
  }

  private updateHiddenInput() {
    const queryStrings = this.queryInputGroups.map((group) => ({
      [group.fieldSelect.value]: group.valueInput.value,
    }))

    this.updateValue(queryStrings)
  }

  updateValue(queryStrings: QueryString[]) {
    this.inputTarget.value = queryStrings
      .map((q) => [Object.keys(q)[0], q[Object.keys(q)[0]]].join(':'))
      .join(',')
  }

  getValue(): QueryString[] {
    return this.inputTarget.value.split(',').map((q) => {
      const [key, value] = q.split(':')
      return { [key]: value }
    })
  }
}
